<template>
	<div class="container-fluid">

		<!---- General comparison widget ---->
		<widget>

			<!-- Not a valid chart analysis? (eg. date) -->
			<div v-if="compareBy == 'date'" class="container">
				<chart
					style="opacity: 0.4"
					:datasets="[]"
					:labels="[]"
					type="bar"
					:height="300">
				</chart>

				<h2 class="centered-text">Cannot compare users by {{compareBy}}</h2>

			</div>


			<!-- Too many users selected? (10 max) -->
			<div v-else-if="users.length > MAX_USER_LENGTH" class="container">
				<chart
					style="opacity: 0.4"
					:datasets="[]"
					:labels="[]"
					type="bar"
					:height="300">
				</chart>

				<h2 class="centered-text">Too many users selected for chart comparison</h2>

			</div>

			<chart
				v-else
				:key="chartVersion"
				:datasets="barChartDataset"
				:labels="usersNames"
				type="bar"
				:height="300">
			</chart>

		</widget>


		<!------- Breakdown of costs (labour, materials, etc) ------->
		<!----- (Only shown when CompareBy: Costs is Selected) ------>
		<widget v-if="compareBy == 'costs'">

			<!-- Too many users selected? (10 max) -->
			<div v-if="users.length > MAX_USER_LENGTH" class="container">
				<chart
					style="opacity: 0.4"
					:datasets="[]"
					:labels="[]"
					type="bar"
					:height="300">
				</chart>

				<h2 class="centered-text">Too many users selected for chart comparison</h2>
			</div>

			<chart
				v-else
				:key="chartVersion"
				:datasets="costsBreakdownDataset"
				:labels="usersNames"
				type="bar"
				:height="300">
			</chart>

		</widget>

	</div>
</template>

<script type="text/javascript">

import Chart from "@/components/Chart"
import Widget from "@/components/Widget"

import Utilities from "@/mixins/Utilities"


export default {
	name: "ServiceReportChartView",
	components: {
		Chart,
		Widget
	},
	mixins: ["Utilities"],
	props: [
		"users",	// List of employees/customers, containing .totalMaterial, .totalRevenue, etc
						// (every value set in ReportsServicesDashboard)
		"compareBy",	// "costs", "quotes"
	],
	data() {
		return {
			MAX_USER_LENGTH: 10,	// show charts when users.length < MAX_USER_LENGTH
			chartVersion: 0				// Increment each time the prop is changed

		}
	},

	computed: {

		usersNames() {
			let prims = this.primitize(this.users, "name")
			return prims
		},

		barChartDataset() {
			if (this.compareBy == "costs")
				return this.revenueDatasets
			else if (this.compareBy == "quotes")
				return this.quoteDatasets
		},

		revenueDatasets() {
			let datasets = [
				{
					label: "Total Costs",
					data: [],
					backgroundColor: 'rgba(54, 162, 235, 0.2)',
					borderColor: 'rgba(54, 162, 235, 1)',
					borderWidth: 1
				},
				{
					label: "Total Revenue",
					data: [],
					backgroundColor: 'rgba(255, 206, 86, 0.2)',
					borderColor: 'rgba(255, 206, 86, 1)',
					borderWidth: 1
				},
				{
					label: "Total Profits",
					data: [],
					backgroundColor: 'rgba(75, 192, 192, 0.2)',
					borderColor: 'rgba(75, 192, 192, 1)',
					borderWidth: 1
				}
			]

			this.users.forEach(emp => {
				datasets[0].data.push(parseFloat(emp.totalCosts))
				datasets[1].data.push(parseFloat(emp.totalRevenue))
				datasets[2].data.push(parseFloat(emp.totalProfits))
			})

			return datasets
		},

		quoteDatasets() {
			let datasets = [
				{
					label: "Quotes Pending",
					data: [],
					backgroundColor: 'rgba(255, 206, 86, 0.2)',
					borderColor: 'rgba(255, 206, 86, 1)',
					borderWidth: 1
				},
				{
					label: "Quotes Declined",
					data: [],
					backgroundColor: 'rgba(255, 99, 132, 0.2)',
					borderColor: 'rgba(255, 99, 132, 1)',
					borderWidth: 1
				},
				{
					label: "Quotes Completed",
					data: [],
					backgroundColor: 'rgba(75, 192, 192, 0.2)',
					borderColor: 'rgba(75, 192, 192, 1)',
					borderWidth: 1
				},
				{
					label: "Total Quotes",
					data: [],
					backgroundColor: 'rgba(54, 162, 235, 0.2)',
					borderColor: 'rgba(54, 162, 235, 1)',
					borderWidth: 1
				}
			]

			this.users.forEach(emp => {
				datasets[0].data.push(parseFloat(emp.quotesPending))
				datasets[1].data.push(parseFloat(emp.quotesDeclined))
				datasets[2].data.push(parseFloat(emp.quotesCompleted))
				datasets[3].data.push(parseFloat(emp.quotesTotal))
			})

			return datasets
		},

		costsBreakdownDataset() {
			let datasets = [
				{
					label: "Total Material Costs",
					data: [],
					backgroundColor: 'rgba(54, 162, 235, 0.2)',
					borderColor: 'rgba(54, 162, 235, 1)',
					borderWidth: 1
				},
				{
					label: "Total Labour Costs",
					data: [],
					backgroundColor: 'rgba(255, 206, 86, 0.2)',
					borderColor: 'rgba(255, 206, 86, 1)',
					borderWidth: 1
				},
				{
					label: "Total Equipment Costs",
					data: [],
					backgroundColor: 'rgba(75, 192, 192, 0.2)',
					borderColor: 'rgba(75, 192, 192, 1)',
					borderWidth: 1
				},
				{
					label: "Total Permit Costs",
					data: [],
					backgroundColor: 'rgba(255, 99, 132, 0.2)',
					borderColor: 'rgba(255, 99, 132, 1)',
					borderWidth: 1
				}
			]

			//    0         1        2         3
			// [material, labour, equipment, permit]
			this.users.forEach(emp => {
				datasets[0].data.push(parseFloat(emp.totalMaterial))
				datasets[1].data.push(parseFloat(emp.totalLabour))
				datasets[2].data.push(parseFloat(emp.totalEquipment))
				datasets[3].data.push(parseFloat(emp.totalPermit))
			})

			return datasets
		},
	},

	watch: {
		users() {
			this.chartVersion += 1
		},
		compareBy() {
			this.chartVersion += 1
		}
	},
	methods: {
	}
}
</script>

<style scoped>

.centered-text {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

</style>
